import {createSlice} from '@reduxjs/toolkit';
import type {PayloadAction} from '@reduxjs/toolkit';
import {Problem, Tournament, TrainingSessionProblem} from '@types';
import {buildGeneralSetState, buildGeneralToggleState} from './general';

type UIState = {
    currentProblem: Problem | null;
    tournament: Tournament | null;
    problemSolutionsCommentsVisible: boolean;
    problemTreeVisible: boolean;
    userRatingRefreshCount: number;
    statusBarUserElo: number | null;
    trainingSolvedCounter: number;
    trainingMissedCounter: number;
    trainingProblemIds: number[] | null;
};

const uiInitialState: UIState = {
    currentProblem: null,
    tournament: null,
    // problemSolutionsCommentsVisible: false,
    problemSolutionsCommentsVisible: false,
    problemTreeVisible: false,
    userRatingRefreshCount: 0,
    statusBarUserElo: null,
    trainingSolvedCounter: 0,
    trainingMissedCounter: 0,
    trainingProblemIds: null,
};

export const uiSlice = createSlice({
    name: 'ui',
    initialState: uiInitialState,
    reducers: {
        setCurrentProblem: buildGeneralSetState('currentProblem'),
        setTournament: buildGeneralSetState('tournament'),
        setProblemSolutionsCommentsVisible: buildGeneralSetState(
            'problemSolutionsCommentsVisible'
        ),
        toggleProblemSolutionsCommentsVisible: buildGeneralToggleState(
            'problemSolutionsCommentsVisible'
        ),
        setProblemTreeVisible: buildGeneralSetState('problemTreeVisible'),
        toggleProblemTreeVisible: buildGeneralToggleState('problemTreeVisible'),
        refreshUserRating: (state: UIState) => {
            state.userRatingRefreshCount++;
        },
        setStatusBarUserElo: (state, action: PayloadAction<number>) => {
            state.statusBarUserElo = action.payload;
        },
        incrementTrainingSolvedCounter: (state: UIState) => {
            state.trainingSolvedCounter++;
        },
        incrementTrainingMissedCounter: (state: UIState) => {
            state.trainingMissedCounter++;
        },
        resetTrainingCounters: (state: UIState) => {
            state.trainingSolvedCounter = 0;
            state.trainingMissedCounter = 0;
        },
        startTrainingSession: (
            state: UIState,
            action: PayloadAction<TrainingSessionProblem[]>
        ) => {
            const uniqueProblems = Array.from(
                new Map(action.payload.map(problem => [problem.problemId, problem])).values()
            );
            state.trainingProblemIds = uniqueProblems.map(
                problem => problem.problemId
            );
        },
        restoreTrainingSession: (state, action: PayloadAction<number[]>) => {
            state.trainingProblemIds = action.payload;
        },
        removeTrainingProblem: (
            state: UIState,
            action: PayloadAction<number>
        ) => {
            state.trainingProblemIds =
                state.trainingProblemIds?.filter(id => id !== action.payload) ??
                null;
        },
        resetTrainingSession: (state: UIState) => {
            state.trainingProblemIds = null;
        },
        recordTrainingResult: (
            state: UIState,
            action: PayloadAction<{solved: boolean}>
        ) => {
            if (action.payload.solved) {
                state.trainingSolvedCounter++;
            } else {
                state.trainingMissedCounter++;
            }
        },
    },
});
